'use client'

import { useState } from 'react'
import { Pencil, ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { AiUseCaseView } from '@/server-actions/ai-use-cases'
import { GuardrailDialog } from './guardrail-dialog'
import { useTranslations } from "next-intl"


interface UseCaseGuardrailSectionProps {
  useCase: AiUseCaseView
  canManage: boolean
}

/**
 * "Platform guardrail" block on a use-case card. Shows presence only — the text is
 * platform config behind `ai.global_configs.manage` and is loaded by the dialog.
 */
export function UseCaseGuardrailSection({ useCase, canManage }: UseCaseGuardrailSectionProps) {
  const tUi = useTranslations("Ai.AiConfigUseCaseGuardrailSection")

  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <div className="py-2 px-3 bg-gray-50 rounded-lg space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-sm text-gray-700"><span key={tUi("platformGuardrail")}>{tUi("platformGuardrail")}</span></span>
          <Badge variant={useCase.has_guardrail ? 'secondary' : 'outline'} className="font-normal">
            <span key={useCase.has_guardrail ? tUi("configured") : tUi("notSet")}>{useCase.has_guardrail ? tUi("configured") : tUi("notSet")}</span>
          </Badge>
        </div>
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1"
            onClick={() => setDialogOpen(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span><span key={tUi("edit")}>{tUi("edit")}</span></span>
          </Button>
        )}
      </div>

      <p className="text-xs text-gray-500">
        <span key={tUi("appendedAfterTheCustomerSPromptOnEvery")}>{tUi("appendedAfterTheCustomerSPromptOnEvery")}</span></p>

      {canManage && (
        <GuardrailDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          useCaseId={useCase.id}
          useCaseSlug={useCase.slug}
          useCaseName={useCase.display_name}
        />
      )}
    </div>
  )
}
